import { DestroyRef, inject, Service, signal } from '@angular/core';
import type { Client, StompSubscription } from '@stomp/stompjs';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { TableEvent } from '../data/table.types';

type StompModule = typeof import('@stomp/stompjs');

/**
 * STOMP wrapper for the table topic (`/ws-qr`, no auth). Connects lazily to a single table,
 * re-subscribes automatically on reconnect (stompjs), and surfaces parsed envelopes on `events$`.
 * Provided at the QR-menu page level so it disconnects when the page injector is destroyed.
 */
@Service({ autoProvided: false })
export class TableSocketService {
  private client: Client | null = null;
  private subscription: StompSubscription | null = null;
  private tableUuid: string | null = null;
  /** Monotonic connect() attempt id — a stale attempt resuming after the lazy stompjs import
   * (or after a disconnect() raced the await) must not build a second live client. */
  private connectSeq = 0;

  private readonly _connected = signal(false);
  /** True while the STOMP session is up and subscribed (the socket is listen-only: all guest
   * actions go over REST, only confirmations arrive here). */
  readonly connected = this._connected.asReadonly();

  private readonly _events = new Subject<TableEvent>();
  /** Parsed `{event,data,timestamp}` envelopes from `/topic/table/{tableUuid}`. */
  readonly events$ = this._events.asObservable();

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.disconnect();
      // Complete only on service teardown — NOT in disconnect(), which also runs on reconnect /
      // table switch, where events$ must keep flowing.
      this._events.complete();
    });
  }

  async connect(tableUuid: string): Promise<void> {
    // No WebSocket in SSR/tests, or no endpoint configured — stay offline.
    if (typeof window === 'undefined' || typeof WebSocket === 'undefined') return;
    if (!environment.wsUrl || !tableUuid) return;
    // Already wired (or wiring — tableUuid is set before the await) to this table.
    if (this.tableUuid === tableUuid) return;

    this.disconnect();
    this.tableUuid = tableUuid;
    const seq = ++this.connectSeq;

    // stompjs is loaded on demand: guests without a table never download it.
    let loaded: StompModule;
    try {
      loaded = await import('@stomp/stompjs');
    } catch {
      // Chunk failed to load (flaky network) — release the uuid so a later connect() can retry.
      if (seq === this.connectSeq) this.tableUuid = null;
      return;
    }
    // Пакет резолвится по условию "browser" в свою UMD-сборку, а именованные экспорты из UMD-фабрики
    // esbuild статически не видит — чанк отдаёт модуль одним default'ом. Статическому import интероп
    // это чинил сам, динамическому — нет: namespace приходил без имён, и Client/ReconnectionTimeMode
    // были undefined (сокет не поднимался вовсе). Ветка без default оставлена на случай, если сборка
    // однажды возьмёт ESM-выход пакета.
    const stomp: StompModule = (loaded as { default?: StompModule }).default ?? loaded;
    const { Client, ReconnectionTimeMode } = stomp;
    // Superseded by a newer connect() or a disconnect() while the chunk was in flight.
    if (seq !== this.connectSeq || this.tableUuid !== tableUuid) return;

    const client = new Client({
      brokerURL: environment.wsUrl,
      // Guide: never set heartbeat to 0 — the server rejects 0,0 at CONNECT. Keep stompjs default.
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      // Retry forever (guests keep the page open), but back off 3s → 30s between attempts.
      reconnectDelay: 3000,
      reconnectTimeMode: ReconnectionTimeMode.EXPONENTIAL,
      maxReconnectDelay: 30000,
      onConnect: () => {
        this._connected.set(true);
        // onConnect fires again after every reconnect — resubscribe on the fresh session.
        this.subscription = client.subscribe(`/topic/table/${tableUuid}`, (msg) => {
          const event = this.parse(msg.body);
          if (event) this._events.next(event);
        });
      },
      onWebSocketClose: () => this._connected.set(false),
      onStompError: () => this._connected.set(false),
    });

    this.client = client;
    client.activate();
  }

  disconnect(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
    const client = this.client;
    this.client = null;
    this.tableUuid = null;
    if (client) {
      // Detach callbacks first — the old socket closes asynchronously, and a late
      // onWebSocketClose from the dying client must not clobber shared state.
      client.onConnect = () => {};
      client.onStompError = () => {};
      client.onWebSocketClose = () => {};
      // deactivate() stops the reconnect loop and closes the socket.
      void client.deactivate();
    }
    this._connected.set(false);
  }

  private parse(body: string): TableEvent | null {
    try {
      const parsed: unknown = JSON.parse(body);
      if (parsed && typeof (parsed as { event?: unknown }).event === 'string') {
        return parsed as TableEvent;
      }
    } catch {
      // Non-JSON / unexpected frame — ignore rather than crash the handler.
    }
    return null;
  }
}
